import { ClockCircleOutlined, PlayCircleFilled } from '@ant-design/icons';
import { CalendarIcon } from '@heroicons/react/outline';
import { ChevronRightIcon } from '@heroicons/react/solid';
import { Avatar } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import ProfileIcons from 'assets/Icons/ProfileIcons';
import clsx from 'clsx';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type React from 'react';
import { type FC, useMemo, useRef, useState } from 'react';

import { getUserInitials } from '@/components/ChatBox/Message';
import LiveYoutubeHomePagePlayer from '@/components/Live/LiveYoutubeHomePagePlayer';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';

import { formatTime } from '@/pages/live/[slug]';
import { keyFromYoutubeLiveLink } from '@/utils/index';
import { IStateHomePage } from '@/utils/interfaces/Live';

export interface RenderAvatarProps {
  containerStyle?: React.CSSProperties;
  wrapperStyle?: string;
  width: number;
  avatarStyle?: React.CSSProperties;
  userName?: string;
  avatarSrc?: string;
  gradientBg?: string;
}

export interface GetCurrentLiveData {
  id: number;
  title: string;
  desc: string;
  registration_count?: number;
  is_registered?: boolean;
  work_shop_details: {
    slug: string;
    title: string;
    id?: number;
    author_name?: string;
    category_name?: string;
    author: {
      experience_years: number;
      avatar: string;
      user_name?: string;
      name: string;
      emp_work_experience: {
        company: string;
        designation: string;
      };
    };
  };
  live_workshop_session_thumbnails: {
    original_url: string;
  };
  start_time: string;
  end_time: string;
  start_date: string;
  live_workshop_id?: number;
}

const RenderAvatar = ({
  containerStyle,
  wrapperStyle,
  width,
  avatarStyle,
  userName,
  avatarSrc,
  gradientBg,
}: RenderAvatarProps) => {
  const avatarBackground = useMemo(
    () => (!avatarSrc ? gradientBg : undefined),
    [avatarSrc, gradientBg],
  );

  const avatarClass = useMemo(
    () => clsx(!avatarSrc ? 'bg-primary' : 'bg-bgColor'),
    [avatarSrc],
  );

  return (
    <div
      style={{ width, height: width, ...containerStyle }}
      className={clsx(wrapperStyle, 'relative bg-white')}
    >
      <Avatar
        size={width}
        alt='profile-picture'
        style={{
          ...avatarStyle,
          background: avatarBackground,
        }}
        className={avatarClass}
        src={avatarSrc}
      >
        {!avatarSrc && !userName ? (
          <ProfileIcons width='8' />
        ) : (
          userName && getUserInitials(userName)
        )}
      </Avatar>
    </div>
  );
};

const LiveBannerCard: FC<{
  item: IStateHomePage;
}> = ({ item }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const mainContentRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const gradientBg = 'linear-gradient(180deg, #FF5612 0%, #C72707 100%)';
  const screens = useBreakpoint();
  const router = useRouter();

  const handlePlay = () => {
    if (
      playerRef.current &&
      typeof playerRef.current.playVideo === 'function'
    ) {
      playerRef.current.playVideo();
    }
  };
  return (
    <div
      onClick={() => {
        if (
          item?.liveSession?.iframe_type === 'google_meet' &&
          typeof window !== 'undefined'
        ) {
          window.open(item?.liveSession?.iframe, '_blank');
        } else {
          router.push(
            `/live/watch/${item?.liveSession?.id}?workshop_id=${item?.liveSession?.id}`,
          );
        }
      }}
      className='flex flex-col gap-y-4 md:gap-y-6 p-10 lg:flex-row h-full text-white rounded-2xl overflow-hidden w-full bg-black'
    >
      <div
        className={clsx(
          item?.liveSession?.iframe_type === 'google_meet' && 'p-10',
          'relative flex-1 w-full lg:w-[65%] 2xl:w-[50%] h-full aspect-[2/1]',
        )}
      >
        <ShouldRender check={item?.liveSession?.iframe_type === 'google_meet'}>
          <Image
            src={item?.liveSession?.thumbnail as string}
            alt={item.liveSession?.title as string}
            layout='fill'
            objectFit='cover'
            className='z-0 rounded-lg'
          />
        </ShouldRender>

        <ShouldRender check={item?.liveSession?.iframe_type === 'youtube'}>
          <ShouldRender check={item?.liveSession?.iframe && !item?.loading}>
            <div
              ref={mainContentRef}
              className='iframe-fullscreen h-full disableYoutubeUI relative group'
            >
              <LiveYoutubeHomePagePlayer
                playerRef={playerRef}
                videoId={keyFromYoutubeLiveLink(item?.liveSession.iframe)}
                setIsPlaying={setIsPlaying}
                isPlaying={isPlaying}
              />

              <ShouldRender check={!isPlaying}>
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePlay();
                  }}
                  className='absolute z-40 cursor-pointer bottom-1/2 right-1/2 translate-x-1/2 translate-y-1/2'
                >
                  <PlayCircleFilled className='text-4xl md:text-5xl hover:text-gray-200 bg-bgColor rounded-full hover:scale-[1.1]' />
                </span>
              </ShouldRender>

              {/* <ShouldRender check={isPlaying}>
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePause();
                }}
                className='absolute z-40 cursor-pointer bottom-1/2 right-1/2 translate-x-1/2 translate-y-1/2 hidden group-hover:block'
              >
                <PauseCircleFilled className='text-4xl md:text-5xl hover:text-gray-200 bg-bgColor rounded-full hover:scale-[1.1]' />
              </span>
            </ShouldRender> */}
            </div>
            <ShouldRender check={isPlaying}>
              <div className='-translate-y-14'>
                <div className='text-base font-semibold text-end mr-12 flex justify-end items-center pb-3 uppercase'>
                  <div className='w-2 h-2 bg-[#DC2626] animate-ping rounded-full mr-2  ' />{' '}
                  Live
                </div>
                <div className='relative  px-4 flex flex-col'>
                  <div className='w-full bg-primary h-[2px]'></div>
                  <div className='h-3 w-3 bg-white rounded-full absolute right-4 -top-[5px]' />
                </div>
              </div>
            </ShouldRender>
          </ShouldRender>
        </ShouldRender>
      </div>
      <div className='flex   lg:pl-8  w-full lg:w-[35%] 2xl:w-[50%] '>
        <div className='flex flex-col justify-center w-full h-full bg-black gap-y-4 md:gap-y-6'>
          <div>
            <div className='bg-cartBgColor text-textLightGray text-sm px-3 mb-2 md:mb-4 py-1 md:py-1.5  rounded-md w-fit'>
              {item?.liveSession?.category}
            </div>
            <h2 className='text-base md:text-2xl xl:text-[32px] font-semibold mb-3 md:mb-2  leading-tight tracking-[-2.5%]'>
              {item?.liveSession?.title}
            </h2>
            <ShouldRender check={screens.md}>
              <p className='text-white text-sm xl:text-base mb-2 md:mb-0 font-light line-clamp-2'>
                {item?.liveSession?.desc}
              </p>

              <div className='flex items-center gap-3 text-gray-300 text-sm md:py-6 '>
                <div className='flex items-center gap-1.5 text-white font-medium text-base lg:text-sm xl:text-base'>
                  <ClockCircleOutlined className='text-[#FFFFFF99]' />{' '}
                  <span>
                    {formatTime(
                      moment(item?.liveSession?.start_time, 'HH:mm:ss'),
                    )}{' '}
                    -{' '}
                    {formatTime(
                      moment(item?.liveSession?.end_time, 'HH:mm:ss'),
                    )}
                  </span>
                </div>
                <span className='text-white'>|</span>
                <div className='flex items-center gap-1.5 text-white font-medium text-base lg:text-sm xl:text-base'>
                  <CalendarIcon className='text-[#FFFFFF99] w-4 h-4' />{' '}
                  <span>
                    {moment(item?.liveSession?.start_date).format(
                      'DD MMM, YYYY',
                    )}
                    ,
                  </span>
                </div>
              </div>
            </ShouldRender>

            <div className='flex items-start gap-3'>
              <RenderAvatar
                wrapperStyle='rounded-full min-w-[40px] min-h-[40px] flex justify-center items-center'
                width={40}
                avatarStyle={{
                  fontSize: '14px',
                  border: '2px solid #FFFFFF',
                }}
                userName={item?.liveSession?.author?.name}
                avatarSrc={`${process.env.NEXT_PUBLIC_STUDIO_PATH || ''}/${item?.liveSession?.author?.profile_data?.avatar}`}
                gradientBg={gradientBg}
              />
              <div>
                {/* <ShouldRender
                  check={
                    item.liveSession?.author?.profile_data?.experience_years &&
                    screens.md
                  }
                >
                  <Button
                    size='small'
                    type='text'
                    style={{
                      position: 'relative',
                      overflow: 'hidden',
                      background:
                        'linear-gradient(180deg, rgba(97, 153, 255, 0.1) 0%, rgba(4, 92, 251, 0.1) 100%)',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '6px 12px',
                      zIndex: 1,
                    }}
                    className='h-[24px] text-[10px] font-semibold hover:text-white'
                  >
                    <span
                      style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: -1,
                        borderRadius: 'inherit',
                        padding: '1.2px',
                        background:
                          'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',
                        WebkitMask:
                          'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                      }}
                    />

                    <span
                      style={{
                        background:
                          'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold',
                      }}
                    >
                      {item.liveSession?.author?.profile_data?.experience_years}{' '}
                      Years of Experience
                    </span>
                  </Button>
                </ShouldRender> */}
                <p className='text-xs hover:underline cursor-pointer  hover:text-white md:text-sm font-light mt-1 xl:mt-1.5'>
                  {item.liveSession?.author?.name}
                </p>
                <ShouldRender
                  check={
                    item?.liveSession?.author?.profile_data?.emp_work_experience
                      ?.company ||
                    item?.liveSession?.author?.profile_data?.emp_work_experience
                      ?.designation
                  }
                >
                  <div className='text-xs text-[#FF5612] font-semibold'>
                    {
                      item?.liveSession?.author?.profile_data
                        ?.emp_work_experience?.designation
                    }{' '}
                    at{' '}
                    {
                      item?.liveSession?.author?.profile_data
                        ?.emp_work_experience?.company
                    }
                  </div>
                </ShouldRender>
              </div>
            </div>
          </div>

          <ButtonDefault
            size={4}
            variant={ButtonVariants.WHITE}
            rounded={false}
            roundedSm={true}
            className='w-full 2xl:w-fit  p-1'
          >
            <span className='flex text-sm md:text-base gap-x-2 2xl:px-10 items-center font-semibold whitespace-nowrap'>
              Join Live Now{' '}
              <ChevronRightIcon className='md:w-5 md:h-5 w-4 h-4' />
            </span>
          </ButtonDefault>
        </div>
      </div>
    </div>
  );
};

export default LiveBannerCard;
